import React from 'react';
import { Box, Typography } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';

export type PlaylistLesson = {
    _id: string;
    title: string;
    is_free_preview?: boolean;
    has_assignment?: boolean;
    project?: string | null;
    has_project?: boolean;
    moduleTitle?: string;
    description?: string;
};

type Props = {
    lessons: PlaylistLesson[];
    currentLessonId: string;
    canWatchAll: boolean;
    completedLessonIds?: Set<string>;
    onSelect: (lessonId: string, isFreePreview?: boolean) => void;
    dense?: boolean;
};

export function SandboxClassPlaylist({
    lessons,
    currentLessonId,
    canWatchAll,
    completedLessonIds,
    onSelect,
    dense,
}: Props) {
    if (!lessons.length) {
        return (
            <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontSize: 13, fontFamily: OBSIDIAN_BODY_FONT, p: 2 }}>
                No classes yet
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {lessons.map((lesson, index) => {
                const locked = !canWatchAll && !lesson.is_free_preview;
                const active = String(lesson._id) === String(currentLessonId);
                const done = completedLessonIds?.has(String(lesson._id));
                return (
                    <Box
                        key={lesson._id}
                        onClick={() => {
                            if (locked) return;
                            onSelect(lesson._id, Boolean(lesson.is_free_preview));
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            p: dense ? 1 : 1.25,
                            borderRadius: 2,
                            cursor: locked ? 'default' : 'pointer',
                            bgcolor: active ? 'rgba(255,201,101,0.12)' : 'transparent',
                            border: active ? `1px solid ${OBSIDIAN.primary}55` : '1px solid transparent',
                            opacity: locked ? 0.55 : 1,
                            transition: 'background 0.15s ease',
                            '&:hover': locked
                                ? undefined
                                : { bgcolor: active ? 'rgba(255,201,101,0.16)' : OBSIDIAN.surfaceContainerHigh },
                        }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: active ? OBSIDIAN.primary : OBSIDIAN.onSurfaceVariant,
                            }}>
                            {locked ? (
                                <LockOutlinedIcon sx={{ fontSize: 16 }} />
                            ) : done ? (
                                <CheckCircleIcon sx={{ fontSize: 18, color: OBSIDIAN.secondary }} />
                            ) : (
                                <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
                            )}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: OBSIDIAN.onSurfaceVariant,
                                    letterSpacing: '0.04em',
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                CLASS {index + 1}
                                {lesson.is_free_preview ? ' · PREVIEW' : ''}
                            </Typography>
                            <Typography
                                noWrap
                                sx={{
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: active ? 700 : 600,
                                    fontSize: 13,
                                    color: OBSIDIAN.onSurface,
                                }}>
                                {lesson.title}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
